/**
 * Lesende KI der Planungsphase — der dünne Aufruf (Plan Stufe 7).
 *
 *  - `liesMerkmale`        → Proxy-Aktion `extractPlanungMerkmale`, je Dokument ein Aufruf.
 *  - `einschaetzeBaubeschreibung` → Proxy-Aktion `einschaetzePlanungBaubeschreibung`.
 *  - `ordneMailAntworten`  → Proxy-Aktion `extractPlanungAntworten`.
 *
 * Beide werfen nie: das Ergebnis ist ein `KiLauf` mit Vorschlägen, den
 * verworfenen Kandidaten und — im Fehlerfall — `fehler` bei leerer
 * Vorschlagsliste. Ein gescheiterter Aufruf ist nie „nichts gefunden".
 * Übernommen wird nichts; das entscheidet der Mensch in der Vorschlagsleiste.
 *
 * Die Prüfung der Antworten steht rein in `auswertung.ts` und ist dort getestet.
 *
 * WICHTIG: Alle drei Aktionen brauchen den Deploy des gemini-proxy
 * (`supabase functions deploy gemini-proxy`) — sonst „Unbekannte Action".
 */

import { callAiBackend } from '../geminiService';
import {
    Verwerfliste,
    baueMerkmalPayload,
    fasseMerkmaleZusammen,
    lauf,
    planeBeschreibungLauf,
    planeMailLauf,
    planeMerkmalLauf,
    werteEinschaetzungAus,
    werteMailAntwortAus,
    werteMerkmalAntwortAus,
} from './auswertung';
import type { KiAntwortVorschlag, KiLauf, KiMerkmalAnfrage, KiMerkmalVorschlag, KiQuelle } from './typen';

export const KI_ABGEBROCHEN = 'Abgebrochen, es wurde nichts übernommen.';

/**
 * Lässt den Aufruf mit dem Signal enden. `callAiBackend` kennt kein Signal:
 * die Anfrage läuft im Hintergrund zu Ende, ihr Ergebnis wird verworfen.
 */
const mitAbbruch = <T>(aufruf: Promise<T>, signal?: AbortSignal): Promise<T> => {
    if (!signal) return aufruf;
    return new Promise<T>((resolve, reject) => {
        const beiAbbruch = () => reject(new Error(KI_ABGEBROCHEN));
        signal.addEventListener('abort', beiAbbruch, { once: true });
        // Handler zuerst anhängen: ein späterer Fehler des verworfenen
        // Aufrufs bleibt so keine unbehandelte Ablehnung.
        aufruf.then(
            (wert) => { signal.removeEventListener('abort', beiAbbruch); resolve(wert); },
            (fehler) => { signal.removeEventListener('abort', beiAbbruch); reject(fehler); },
        );
        if (signal.aborted) beiAbbruch();
    });
};

const meldung = (e: unknown): string => (e as Error)?.message || 'Die KI-Anfrage ist gescheitert.';

/**
 * Liest Merkmale aus den ausgewählten Unterlagen. Ein Dokument nach dem
 * anderen: scheitert eines, endet der Lauf mit `fehler` (spart die übrigen
 * Aufrufe) und ohne Vorschläge.
 */
export const liesMerkmale = async (
    quellen: KiQuelle[],
    merkmale: KiMerkmalAnfrage[],
    opt: { signal?: AbortSignal } = {},
): Promise<KiLauf<KiMerkmalVorschlag>> => {
    const verworfen = new Verwerfliste();
    const plan = planeMerkmalLauf(quellen, merkmale, verworfen);
    if (plan.fehler) return lauf<KiMerkmalVorschlag>([], verworfen, plan.fehler);

    const roh: Parameters<typeof fasseMerkmaleZusammen>[0] = [];
    for (const auftrag of plan.auftraege) {
        if (opt.signal?.aborted) return lauf<KiMerkmalVorschlag>([], verworfen, KI_ABGEBROCHEN);
        let antwort: unknown;
        try {
            antwort = await mitAbbruch(
                callAiBackend('extractPlanungMerkmale', baueMerkmalPayload(auftrag, plan.merkmale)),
                opt.signal,
            );
        } catch (e) {
            if (opt.signal?.aborted) return lauf<KiMerkmalVorschlag>([], verworfen, KI_ABGEBROCHEN);
            return lauf<KiMerkmalVorschlag>([], verworfen, `„${auftrag.quelle.dokument}": ${meldung(e)}`);
        }
        const ergebnis = werteMerkmalAntwortAus(antwort, auftrag, plan.merkmale, verworfen);
        if (ergebnis.fehler) return lauf<KiMerkmalVorschlag>([], verworfen, ergebnis.fehler);
        roh.push(...ergebnis.vorschlaege);
    }
    return lauf(fasseMerkmaleZusammen(roh), verworfen, null);
};

/**
 * Schätzt die offenen Merkmale aus der Baubeschreibung der Grundlagen
 * (Proxy-Aktion `einschaetzePlanungBaubeschreibung`). Ja und Zahlen nur mit
 * geprüftem Zitat, ein Nein auch als Einschätzung mit Begründung — Regeln in
 * `werteEinschaetzungAus`.
 */
export const einschaetzeBaubeschreibung = async (
    text: string,
    merkmale: KiMerkmalAnfrage[],
    opt: { signal?: AbortSignal } = {},
): Promise<KiLauf<KiMerkmalVorschlag>> => {
    const verworfen = new Verwerfliste();
    const plan = planeBeschreibungLauf(text, merkmale, verworfen);
    if (!plan.payload || !plan.auftrag) return lauf<KiMerkmalVorschlag>([], verworfen, plan.fehler);
    if (opt.signal?.aborted) return lauf<KiMerkmalVorschlag>([], verworfen, KI_ABGEBROCHEN);

    let antwort: unknown;
    try {
        antwort = await mitAbbruch(callAiBackend('einschaetzePlanungBaubeschreibung', plan.payload), opt.signal);
    } catch (e) {
        return lauf<KiMerkmalVorschlag>([], verworfen, opt.signal?.aborted ? KI_ABGEBROCHEN : meldung(e));
    }
    const ergebnis = werteEinschaetzungAus(antwort, plan.auftrag, plan.merkmale, verworfen);
    return lauf(fasseMerkmaleZusammen(ergebnis.vorschlaege), verworfen, ergebnis.fehler);
};

/** Ordnet Ausschnitte einer eingefügten Antwortmail den offenen Fragen zu. */
export const ordneMailAntworten = async (
    mailText: string,
    fragen: { code: string; frage: string }[],
    opt: { signal?: AbortSignal } = {},
): Promise<KiLauf<KiAntwortVorschlag>> => {
    const verworfen = new Verwerfliste();
    const plan = planeMailLauf(mailText, fragen, verworfen);
    if (!plan.payload) return lauf<KiAntwortVorschlag>([], verworfen, plan.fehler);
    if (opt.signal?.aborted) return lauf<KiAntwortVorschlag>([], verworfen, KI_ABGEBROCHEN);

    let antwort: unknown;
    try {
        antwort = await mitAbbruch(callAiBackend('extractPlanungAntworten', plan.payload), opt.signal);
    } catch (e) {
        return lauf<KiAntwortVorschlag>([], verworfen, opt.signal?.aborted ? KI_ABGEBROCHEN : meldung(e));
    }
    const ergebnis = werteMailAntwortAus(antwort, plan.payload, verworfen);
    return lauf(ergebnis.vorschlaege, verworfen, ergebnis.fehler);
};
